import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Heart,
  ShoppingBag,
  Scissors,
  Wallet,
  Wind,
  Stamp,
  Truck,
  RotateCcw,
  MapPin,
  Check,
  Star,
} from "lucide-react";
import { AnnouncementBar } from "../components/AnnouncementBar";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Eyebrow, SerifH } from "../components/shared";
import { PRODUCTS, REVIEWS, RATING_DISTRIBUTION } from "../data/content";
import { useCart } from "../context/CartContext";
import { trackEvent } from "../lib/analytics";
import { toAnalyticsItem, parsePriceEur, CURRENCY } from "../lib/catalog";

const SIZES = ["S", "M", "L", "XL", "XXL"];

const DETAIL_TILES = [
  { src: "/yaba_detail_sfifa.jpg", label: "Hand-stitched sfifa", note: "Real gold-thread trim, 6h in Fes." },
  { src: "/yaba_detail_weave.jpg", label: "Premium viscose weave", note: "Fluid drape, silk-like touch." },
  { src: "/yaba_detail_hem.jpg",   label: "Reinforced hem",       note: "Machine-wash safe, holds shape." },
];

const CARE_BULLETS = [
  { Icon: Scissors, txt: "Handcrafted sfifa · gold-thread trim, Fes" },
  { Icon: Wallet,   txt: "Two deep hidden pockets" },
  { Icon: Wind,     txt: "Side-vent airflow, loose fit" },
  { Icon: Stamp,    txt: "Limited run · no two identical" },
];

const DELIVERY_BULLETS = [
  { Icon: Truck,    txt: "Free insured shipping over €200 (DHL Express)" },
  { Icon: RotateCcw, txt: "30-day easy returns · no questions" },
  { Icon: MapPin,   txt: "Ships from Casablanca in 24h" },
];

function Accordion({ title, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#0C111C]/12" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span
          className="text-[11px] tracking-[0.22em] uppercase text-[#0C111C]"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {title}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div
              className="pb-6 text-sm leading-relaxed text-[#3A3A3E]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ZoomableImage({ src, alt }) {
  const ref = useRef(null);
  const [zoom, setZoom] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({ x, y });
  };
  return (
    <div
      ref={ref}
      onMouseEnter={() => setZoom(true)}
      onMouseLeave={() => setZoom(false)}
      onMouseMove={onMove}
      className="relative bg-[#EDE2CE] overflow-hidden aspect-[558/1000] w-full cursor-zoom-in"
      data-testid="pdp-main-image"
    >
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out"
        style={{
          transform: zoom ? "scale(2)" : "scale(1)",
          transformOrigin: `${pos.x}% ${pos.y}%`,
        }}
      />
      <span
        className="absolute bottom-4 right-4 text-[10px] tracking-[0.25em] uppercase bg-[#0C182E]/70 text-[#F4EDE2] px-2.5 py-1 backdrop-blur-sm pointer-events-none"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        Hover to zoom
      </span>
    </div>
  );
}

function StarRow({ count = 5, size = 12 }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-[#C8A96A]">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          fill={i < count ? "#C8A96A" : "none"}
          stroke="#C8A96A"
          strokeWidth={1.5}
        />
      ))}
    </span>
  );
}

function ReviewsSection() {
  const total = 128;
  const avg = 4.9;
  return (
    <section className="bg-[#F4EDE2] border-t border-[#0C111C]/10 py-24 md:py-32" data-testid="pdp-reviews">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-12 lg:gap-20">
          {/* Summary column */}
          <div>
            <Eyebrow>Worn & reviewed</Eyebrow>
            <SerifH className="mt-4" size="clamp(28px, 3vw, 44px)">
              {avg} out of 5.
            </SerifH>
            <div className="mt-3 flex items-center gap-3">
              <StarRow count={5} size={18} />
              <span className="text-sm text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
                Based on {total} verified purchases
              </span>
            </div>

            <div className="mt-8 space-y-2.5">
              {RATING_DISTRIBUTION.map((r) => (
                <div
                  key={r.stars}
                  className="flex items-center gap-3 text-xs"
                  style={{ fontFamily: "Inter, sans-serif" }}
                  data-testid={`pdp-rating-row-${r.stars}`}
                >
                  <span className="w-6 text-[#0C111C]">{r.stars}★</span>
                  <div className="relative flex-1 h-1.5 bg-[#0C111C]/8 overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-[#C8A96A]"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${r.pct}%` }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                  <span className="w-8 text-right text-[#3A3A3E]">{r.pct}%</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              data-testid="pdp-write-review"
              className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#0C111C] underline underline-offset-4 decoration-[#C8A96A] hover:text-[#C8A96A]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Write a review →
            </button>
          </div>

          {/* Testimonials column */}
          <div className="space-y-6 md:space-y-8">
            {REVIEWS.map((r, i) => (
              <motion.article
                key={r.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`pdp-review-${i}`}
                className="relative pl-6 md:pl-8 border-l border-[#C8A96A]/50"
              >
                <div className="flex items-center gap-3">
                  <StarRow count={r.rating} />
                  <span
                    className="text-[10px] tracking-[0.22em] uppercase text-[#C8A96A]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Verified purchase
                  </span>
                </div>
                <h3
                  className="mt-3 text-xl md:text-2xl leading-snug"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  "{r.title}"
                </h3>
                <p
                  className="mt-3 text-[15px] leading-relaxed text-[#3A3A3E]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {r.body}
                </p>
                <p
                  className="mt-4 text-xs text-[#0C111C]/70"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <span className="font-semibold text-[#0C111C]">{r.name}</span>
                  <span className="mx-2 text-[#C8A96A]">·</span>
                  {r.city}
                  <span className="mx-2 text-[#0C111C]/30">·</span>
                  {r.date}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = useMemo(() => PRODUCTS.find((p) => p.slug === slug), [slug]);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [wishlisted, setWishlisted] = useState(false);
  const [added, setAdded] = useState(false);
  const [showSize, setShowSize] = useState(false);
  const [giftEnabled, setGiftEnabled] = useState(false);
  const [giftNote, setGiftNote] = useState("");
  const GIFT_MAX = 80;
  const { addItem } = useCart();

  // scroll to top on slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Track view_item once the product data is resolved.
  useEffect(() => {
    if (!product) return;
    trackEvent("view_item", {
      currency: CURRENCY,
      value: parsePriceEur(product.price),
      items: [toAnalyticsItem(product, 1, undefined, "M")],
    });
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F4EDE2] text-[#0C111C]">
        <AnnouncementBar />
        <Nav />
        <div className="mx-auto max-w-[900px] px-6 py-32 text-center">
          <SerifH size="clamp(28px, 3vw, 44px)">Product not found.</SerifH>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-sm underline underline-offset-4 decoration-[#C8A96A]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <ChevronLeft size={14} /> Back to the collection
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 3);
  const isNewDrop = product.tag === "NEW DROP";
  const scarcity = product.tag === "Only 3 left" ? "Only 3 left in this run"
                   : isNewDrop ? "Just dropped · limited run of 47"
                   : "Low stock alert on select sizes";

  const addToBag = () => {
    addItem({
      slug: product.slug,
      size,
      qty,
      gift: giftEnabled && giftNote.trim() ? giftNote.trim() : null,
    });
    trackEvent("add_to_cart", {
      currency: CURRENCY,
      value: parsePriceEur(product.price) * qty,
      items: [toAnalyticsItem(product, qty, undefined, size)],
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="bg-[#F4EDE2] text-[#0C111C] min-h-screen" data-testid="product-page">
      <AnnouncementBar />
      <Nav />

      {/* breadcrumb */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 pt-6 md:pt-10">
        <nav
          data-testid="pdp-breadcrumb"
          className="text-[11px] tracking-[0.2em] uppercase text-[#3A3A3E] flex items-center gap-2"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <Link to="/" className="hover:text-[#C8A96A] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/#the-collection" className="hover:text-[#C8A96A] transition-colors">Collection</Link>
          <span>/</span>
          <span className="text-[#0C111C]">{product.name}</span>
        </nav>
      </div>

      {/* Editorial split: image left · sticky buy right */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 pt-8 md:pt-10 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-10 lg:gap-16">
        <div>
          <ZoomableImage src={product.image} alt={`${product.name} — ${product.print}`} />

          {/* small thumb strip */}
          <div className="mt-4 grid grid-cols-4 gap-3">
            <button
              type="button"
              className="aspect-square bg-[#EDE2CE] overflow-hidden ring-1 ring-[#C8A96A]"
              data-testid="pdp-thumb-main"
              aria-label="Main view"
            >
              <img src={product.image} alt="" className="h-full w-full object-cover" />
            </button>
            {DETAIL_TILES.map((t, i) => (
              <div
                key={i}
                className="aspect-square bg-[#EDE2CE] overflow-hidden ring-1 ring-[#0C111C]/10"
                data-testid={`pdp-thumb-${i}`}
              >
                <img src={t.src} alt={t.label} className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </div>

        {/* Sticky buy panel */}
        <aside className="lg:sticky lg:top-28 lg:self-start" data-testid="pdp-buy-panel">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase" style={{ fontFamily: "Inter, sans-serif" }}>
            {isNewDrop && (
              <span className="bg-[#C8A96A] text-[#0C182E] px-2 py-0.5">New drop</span>
            )}
            <span className="text-[#3A3A3E]">{product.print}</span>
          </div>

          <SerifH className="mt-4" size="clamp(36px, 3.6vw, 56px)">
            {product.name}
          </SerifH>

          <div className="mt-3 flex items-center gap-3">
            <StarRow />
            <span className="text-xs text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
              4.9 · 128 reviews
            </span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-3xl" style={{ fontFamily: "Fraunces, serif" }}>
              {product.price}
            </span>
            <span className="text-xs text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
              Taxes included · Free EU shipping over €200
            </span>
          </div>

          <p
            className="mt-6 text-[15px] leading-relaxed text-[#3A3A3E]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {product.lead}
          </p>
          <p
            className="mt-3 text-[14px] leading-relaxed text-[#3A3A3E]/85"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Relaxed-fit modern jellaba in premium viscose, hand-finished in Fes.
            Real gold-thread sfifa, deep hidden pockets, breathable side-vents.
            Machine-washable — no dry-clean tax.
          </p>

          {/* Size */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <span
                className="text-[11px] tracking-[0.22em] uppercase text-[#0C111C]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Size · {size}
              </span>
              <button
                type="button"
                onClick={() => setShowSize((v) => !v)}
                data-testid="pdp-size-guide"
                className="text-[11px] tracking-[0.22em] uppercase text-[#0C111C]/60 hover:text-[#C8A96A] underline underline-offset-4 decoration-[#C8A96A]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Size guide
              </button>
            </div>
            <div className="mt-3 grid grid-cols-5 gap-2">
              {SIZES.map((s) => {
                const active = s === size;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSize(s)}
                    data-testid={`pdp-size-${s}`}
                    className={`h-12 border transition-colors text-sm ${
                      active
                        ? "border-[#0C182E] bg-[#0C182E] text-[#F4EDE2]"
                        : "border-[#0C111C]/20 hover:border-[#0C182E] text-[#0C111C]"
                    }`}
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <AnimatePresence>
              {showSize && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden mt-3 text-xs text-[#3A3A3E]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                  data-testid="pdp-size-chart"
                >
                  <div className="grid grid-cols-4 gap-2 border border-[#0C111C]/10 p-4">
                    <div className="font-semibold">Size</div>
                    <div className="font-semibold">Chest (cm)</div>
                    <div className="font-semibold">Length (cm)</div>
                    <div className="font-semibold">Fits</div>
                    {[
                      ["S", "104", "138", "1.65–1.72m"],
                      ["M", "110", "142", "1.72–1.80m"],
                      ["L", "116", "146", "1.80–1.86m"],
                      ["XL", "122", "150", "1.86–1.92m"],
                      ["XXL", "128", "154", "1.92m+"],
                    ].map((r) => (
                      r.map((c, i) => <div key={r[0] + i}>{c}</div>)
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Gift note */}
          <div className="mt-6 border border-[#0C111C]/12 bg-[#EDE2CE]/40 backdrop-blur-sm" data-testid="pdp-gift-block">
            <label className="flex items-start gap-3 px-4 py-3 cursor-pointer">
              <span className="relative mt-0.5 shrink-0">
                <input
                  type="checkbox"
                  checked={giftEnabled}
                  onChange={(e) => {
                    setGiftEnabled(e.target.checked);
                    if (!e.target.checked) setGiftNote("");
                  }}
                  data-testid="pdp-gift-toggle"
                  className="peer sr-only"
                />
                <span
                  className="block w-4 h-4 border border-[#0C111C]/30 peer-checked:border-[#C8A96A] peer-checked:bg-[#C8A96A] transition-colors"
                  aria-hidden="true"
                />
                <Check
                  size={12}
                  strokeWidth={3}
                  className="absolute inset-0 m-auto text-[#0C182E] opacity-0 peer-checked:opacity-100 transition-opacity"
                  aria-hidden="true"
                />
              </span>
              <span className="flex-1">
                <span
                  className="block text-[11px] tracking-[0.22em] uppercase text-[#0C111C]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  Add a gift note
                </span>
                <span
                  className="mt-1 block text-xs text-[#3A3A3E]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  Printed on the thank-you card inside the box. Free.
                </span>
              </span>
            </label>
            <AnimatePresence initial={false}>
              {giftEnabled && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4">
                    <textarea
                      value={giftNote}
                      onChange={(e) =>
                        setGiftNote(e.target.value.slice(0, GIFT_MAX))
                      }
                      rows={2}
                      maxLength={GIFT_MAX}
                      placeholder={'e.g. "For Amine — with love, R."'}
                      data-testid="pdp-gift-input"
                      className="w-full resize-none bg-transparent border-b border-[#0C111C]/25 focus:border-[#0C182E] outline-none py-2 text-sm italic"
                      style={{ fontFamily: "Fraunces, serif" }}
                    />
                    <div
                      className="mt-2 flex items-center justify-between text-[10px] tracking-[0.2em] uppercase"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      <span className="text-[#C8A96A]">
                        Printed in brass on cream
                      </span>
                      <span
                        data-testid="pdp-gift-counter"
                        className={
                          giftNote.length >= GIFT_MAX
                            ? "text-[#B95841]"
                            : "text-[#0C111C]/50"
                        }
                      >
                        {giftNote.length} / {GIFT_MAX}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Qty + Add to bag */}
          <div className="mt-6 flex items-stretch gap-3">
            <div className="flex items-center border border-[#0C111C]/20">
              <button
                type="button"
                aria-label="Decrease quantity"
                data-testid="pdp-qty-dec"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-12 h-14 text-lg hover:bg-[#0C111C]/5"
              >
                −
              </button>
              <span
                className="w-10 text-center text-base"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {qty}
              </span>
              <button
                type="button"
                aria-label="Increase quantity"
                data-testid="pdp-qty-inc"
                onClick={() => setQty((q) => Math.min(9, q + 1))}
                className="w-12 h-14 text-lg hover:bg-[#0C111C]/5"
              >
                +
              </button>
            </div>
            <button
              type="button"
              data-testid="pdp-add-to-bag"
              onClick={addToBag}
              className="flex-1 relative overflow-hidden bg-[#0C182E] text-[#F4EDE2] hover:bg-[#0C111C] transition-colors text-sm tracking-[0.22em] uppercase px-6 h-14"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {added ? (
                  <motion.span
                    key="ok"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="inline-flex items-center gap-2 text-[#C8A96A]"
                    data-testid="pdp-add-to-bag-confirm"
                  >
                    <Check size={16} />
                    {giftEnabled && giftNote.trim() ? "Added with gift note" : "Added to bag"}
                  </motion.span>
                ) : (
                  <motion.span
                    key="idle"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="inline-flex items-center gap-2"
                  >
                    <ShoppingBag size={16} /> Add to bag — {product.price}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            <button
              type="button"
              onClick={() => setWishlisted((v) => !v)}
              aria-label="Wishlist"
              data-testid="pdp-wishlist"
              className={`w-14 h-14 border transition-colors ${
                wishlisted
                  ? "border-[#B95841] text-[#B95841]"
                  : "border-[#0C111C]/20 text-[#0C111C] hover:border-[#B95841] hover:text-[#B95841]"
              }`}
            >
              <Heart
                size={18}
                strokeWidth={1.5}
                className="mx-auto"
                fill={wishlisted ? "#B95841" : "none"}
              />
            </button>
          </div>

          {/* Scarcity + reassurance */}
          <div className="mt-5 flex items-center gap-2 text-[12px] tracking-[0.15em] uppercase text-[#B95841]"
               style={{ fontFamily: "Inter, sans-serif" }}
               data-testid="pdp-scarcity">
            <span className="inline-block w-2 h-2 rounded-full bg-[#B95841] animate-pulse" />
            {scarcity}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2 text-[13px] text-[#3A3A3E]"
               style={{ fontFamily: "Inter, sans-serif" }}>
            {DELIVERY_BULLETS.map(({ Icon, txt }) => (
              <div key={txt} className="flex items-center gap-2">
                <Icon size={14} className="text-[#C8A96A] shrink-0" />
                <span>{txt}</span>
              </div>
            ))}
          </div>

          {/* Accordions */}
          <div className="mt-10">
            <Accordion title="What makes it YABA" defaultOpen testId="pdp-acc-craft">
              <ul className="space-y-2.5">
                {CARE_BULLETS.map(({ Icon, txt }) => (
                  <li key={txt} className="flex items-start gap-3">
                    <Icon size={14} className="text-[#C8A96A] mt-1 shrink-0" />
                    <span>{txt}</span>
                  </li>
                ))}
              </ul>
            </Accordion>
            <Accordion title="Fabric & care" testId="pdp-acc-care">
              <p>100% premium viscose. Machine wash cold on delicates cycle. Line dry.
                Iron on low. No dry-clean tax. Real gold-thread sfifa — trim
                gently by hand if needed.</p>
            </Accordion>
            <Accordion title="Shipping & returns" testId="pdp-acc-shipping">
              <p>Ships in 24h from Casablanca via insured DHL Express. EU delivery
                2–4 business days · Worldwide 3–6 business days. 30-day easy
                returns — no questions, no restocking fee.</p>
            </Accordion>
            <Accordion title="Made in Morocco" testId="pdp-acc-made">
              <p>Cut and hand-finished by our atelier partners in Fes and
                Marrakech. Every jellaba passes 4 QC touch-points. Names of the
                artisans are stitched into the inner label.</p>
            </Accordion>
          </div>
        </aside>
      </section>

      {/* Craft detail row */}
      <section className="bg-[#EDE2CE] py-20 md:py-28" data-testid="pdp-craft-detail">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Eyebrow>Look closer</Eyebrow>
              <SerifH className="mt-4" size="clamp(28px, 3vw, 44px)">
                Every detail earns its place.
              </SerifH>
            </div>
            <p className="max-w-md text-sm text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
              What separates a €40 jellaba from ours isn't the print — it's the
              six hours of hand-finishing you'll never see, but feel every time
              you wear it.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {DETAIL_TILES.map((t, i) => (
              <motion.figure
                key={t.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`pdp-detail-tile-${i}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#F4EDE2]">
                  <img
                    src={t.src}
                    alt={t.label}
                    className="h-full w-full object-cover transition-transform duration-[900ms] hover:scale-[1.04]"
                  />
                </div>
                <figcaption className="mt-4">
                  <p className="text-lg" style={{ fontFamily: "Fraunces, serif" }}>
                    {t.label}
                  </p>
                  <p className="mt-1 text-sm text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
                    {t.note}
                  </p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Craft story video */}
      <section className="bg-[#0C182E] text-[#F4EDE2] py-24 md:py-32" data-testid="pdp-story">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow className="text-[#C8A96A]">The making</Eyebrow>
            <SerifH className="mt-4 text-[#F4EDE2]" size="clamp(30px, 3.4vw, 52px)">
              Six hours in Fes.<br/>One jellaba you'll keep for years.
            </SerifH>
            <p className="mt-6 text-[#F4EDE2]/70 max-w-lg" style={{ fontFamily: "Inter, sans-serif" }}>
              Cut on the second floor of an atelier off Talaa Kebira. Hand-stitched
              sfifa — real gold thread — by artisans whose names we sew into every
              label. Watch a piece come to life.
            </p>
            <Link
              to="/#craftsmanship"
              className="mt-8 inline-flex items-center gap-2 text-sm underline underline-offset-4 decoration-[#C8A96A] hover:text-[#C8A96A]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Read the full story <ChevronLeft size={14} className="rotate-180" />
            </Link>
          </div>
          <div className="aspect-[9/12] md:aspect-[4/5] w-full overflow-hidden">
            <video
              src="/yaba_video.mp4"
              className="h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              data-testid="pdp-story-video"
            />
          </div>
        </div>
      </section>

      <ReviewsSection />

      {/* Pair it with */}
      <section className="bg-[#F4EDE2] py-24 md:py-32" data-testid="pdp-pair-with">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
          <div className="flex items-end justify-between gap-6">
            <div>
              <Eyebrow>Pair it with</Eyebrow>
              <SerifH className="mt-4" size="clamp(28px, 3vw, 44px)">
                Wears well together.
              </SerifH>
            </div>
            <Link
              to="/#the-collection"
              className="hidden md:inline-flex text-sm underline underline-offset-4 decoration-[#C8A96A] hover:text-[#B95841]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              See the full range →
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {related.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
              >
                <Link
                  to={`/product/${p.slug}`}
                  data-testid={`pdp-related-${p.slug}`}
                  className="group block"
                >
                  <div className="aspect-[558/1000] overflow-hidden bg-[#EDE2CE]">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="mt-5 flex items-baseline justify-between">
                    <div>
                      <p className="text-lg" style={{ fontFamily: "Fraunces, serif" }}>
                        {p.name}
                      </p>
                      <p className="text-sm text-[#4A3626]/80" style={{ fontFamily: "Inter, sans-serif" }}>
                        {p.print}
                      </p>
                    </div>
                    <p className="text-lg text-[#4A3626]" style={{ fontFamily: "Fraunces, serif" }}>
                      {p.price}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Sticky mobile buy bar */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#F4EDE2]/95 backdrop-blur-md border-t border-[#0C111C]/10 px-4 py-3 flex items-center gap-3"
        data-testid="pdp-mobile-bar"
      >
        <div className="flex-1">
          <p className="text-xs text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
            {product.name} · {size}
          </p>
          <p className="text-lg" style={{ fontFamily: "Fraunces, serif" }}>
            {product.price}
          </p>
        </div>
        <button
          type="button"
          onClick={addToBag}
          data-testid="pdp-mobile-add"
          className="bg-[#0C182E] text-[#F4EDE2] px-5 h-12 text-sm tracking-[0.2em] uppercase"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {added ? (giftEnabled && giftNote.trim() ? "With note ✓" : "Added ✓") : "Add to bag"}
        </button>
      </div>
    </div>
  );
}
